import { HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { datadogLogs } from '@datadog/browser-logs';
import { EMPTY, Observable } from 'rxjs';

import { TutorialNetworkGuardService } from '../../shared/service/tutorial-network-guard.service';

/**
 * Requests a guided walkthrough is allowed to make. Everything else under `api/` is refused while a
 * walkthrough is running.
 *
 * Deliberately a short, explicit list rather than a list of things to block: a blocklist silently
 * gains a hole every time someone adds an endpoint, which is the failure this interceptor exists to
 * prevent.
 */
const TUTORIAL_ALLOWED_PREFIXES: readonly string[] = [
	// The two preference endpoints — "has this user been offered this walkthrough" and the answer.
	'api/WorkflowTutorial/'

	// The one configuration read, so a walkthrough can show the agency's real workflow without
	// creating the draft that normally carries that configuration (S2N-3089 AC 49/50).
	, 'api/Inventory/MedUse/GetTutorialConfig'

	// Authentication and session. Not operational data, and blocking these would be actively
	// harmful: `Logout` is how an inactivity timeout ends a session, and swallowing it would leave a
	// timed-out session alive behind the overlay. `GetUserInfo` is how the app recovers its security
	// context after a 401/403. Everything else under this prefix — login, password reset — cannot be
	// reached while a walkthrough owns the screen.
	, 'api/Account/'

	// Switching the active agency. Same category as the above -- it establishes which agency the
	// session belongs to, and it writes nothing operational. Blocking it would be actively wrong:
	// the swap ends with a full page reload, which is how S2N-3089 AC 60/61 get their "clean up
	// first, then follow normal agency-isolation behaviour" for free. A silently refused swap would
	// leave the user in the old agency with no explanation.
	, 'api/SetClient'
];

/**
 * Refuses operational HTTP while a guided walkthrough is running (S2N-3089 AC 50/51/52).
 *
 * See {@link TutorialNetworkGuardService} for why this exists alongside the per-handler guards, and
 * why it is not redundant with them.
 *
 * ### What "refused" means
 *
 * The request is never handed to the next handler, and the caller's observable completes without
 * emitting. Not an error: an error would run the caller's error branch, which on these screens means
 * a toast, a navigation, or a "something went wrong" banner appearing in the middle of a lesson. A
 * blocked call means an operational handler ran when its guard should have stopped it, and the right
 * user-visible outcome for that is the same nothing the guard would have produced.
 *
 * That is not the same as swallowing it. Every block is logged as an error to the console and to
 * Datadog, naming the method and URL, because a block in production is a defect in the per-handler
 * guards and needs to be findable. Registered FIRST, ahead of `HttpClientInterceptor`, so a refused
 * request never increments the spinner count or rewrites its URL.
 */
@Injectable()
export class TutorialHttpGuardInterceptor implements HttpInterceptor {
	private readonly guard = inject(TutorialNetworkGuardService);

	intercept(
		req: HttpRequest<unknown>
		, next: HttpHandler
	): Observable<never> | ReturnType<HttpHandler['handle']> {
		if (!this.guard.armed()) { return next.handle(req); }
		if (!isApiRequest(req.url)) { return next.handle(req); }
		if (isAllowedDuringTutorial(req.url)) { return next.handle(req); }

		const message = `Blocked ${req.method} ${req.url} during a guided walkthrough.`
			+ ' An operational handler ran that should have been guarded.';
		// eslint-disable-next-line no-console
		console.error(message);
		datadogLogs.logger.error(message);

		return EMPTY;
	}
}

/**
 * Only S2's own API is policed. Assets, templates, uploaded files and third-party URLs are not
 * operational writes and blocking them would break the walkthrough's own rendering.
 */
function isApiRequest(url: string): boolean {
	return url.startsWith('api/')
		|| url.startsWith('/api/');
}

/** Whether `url` is on the allowlist, tolerating the optional leading slash S2 uses inconsistently. */
function isAllowedDuringTutorial(url: string): boolean {
	const normalized = url.startsWith('/')
		? url.slice(1)
		: url;
	return TUTORIAL_ALLOWED_PREFIXES.some(prefix => normalized.startsWith(prefix));
}
